import { Component, OnInit, Input } from '@angular/core';
import { AlertController, ModalController, PopoverController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { FilePage } from 'src/app/pop-overs/files/file/file.page';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import { MissingInfoTypes } from '../MissingInfoTypes';
import { DetailsPage as ProjectDetailsPage } from 'src/app/projects/details/details.page';
import { DetailsPage as TaskDetailsPage } from 'src/app/tasks/details/details.page';
import { DetailsPage as EmailDetailsPage } from 'src/app/secure-email/details/details.page';
import { CreatePage } from '../create/create.page';

@Component({
  selector: 'app-missing-info-details',
  templateUrl: './details.page.html',
  styleUrls: ['./details.page.scss'],
})
export class DetailsPage implements OnInit {

  @Input() guid: string = '';

  missing_info: any = null;
  team_members: any[] = [];
  loaded: boolean = false;
  refresh: boolean = false;

  // Resolved display names
  client_entity_name: string = '';
  project_title: string = '';
  task_title: string = '';
  recorded_by_name: string = '';
  intended_recipient_name: string = '';
  completed_by_name: string = '';
  missing_info_type: string = '';

  constructor(
    private modalController: ModalController,
    private apiService: ApiService,
    public globalService: GlobalService,
    private popoverController: PopoverController,
    private alertController: AlertController
  ) {}

  ngOnInit() {
    this.GetData();
  }

  async GetData() {
    this.loaded = false;
    await this.GetTeamMembers();

    const data: any = await this.apiService.get(`missing_info/${this.guid}`);
    if (data.success && data.data && data.data[0]) {
      this.missing_info = data.data[0];
      await this.ResolveNames();
      await this.LoadEmailFiles();
      await this.LoadLinkedEmail();
    }
    this.loaded = true;
  }

  // The linked document/question request email, for showing the question, the client's answer and
  // signature, and (for document requests) the files the client uploaded.
  linked_email: any = null;
  document_request_files: any[] = [];

  get isQuestionRequest(): boolean {
    return this.missing_info?.type == 12 || this.missing_info?.type == MissingInfoTypes.outstanding_question;
  }

  // Files the client uploaded: per-document uploads from the document request, plus anything else
  // the email marks as an outside (client) upload. Same sources the secure email details page uses.
  get clientFiles(): any[] {
    const files = new Map<string, any>();
    for (const f of this.document_request_files) files.set(f.guid, f);
    for (const f of this.email_files) {
      if (f.email_files?.[0]?.outside_upload && !files.has(f.guid)) files.set(f.guid, f);
    }
    return Array.from(files.values());
  }

  async LoadLinkedEmail() {
    this.linked_email = null;
    this.document_request_files = [];
    if (!this.missing_info?.email_guid) return;

    const email_data: any = await this.apiService.get(`emails/${this.missing_info.email_guid}`);
    if (email_data?.success && email_data.data && email_data.data[0]) this.linked_email = email_data.data[0];

    if (this.isQuestionRequest) return;
    const docs: any = await this.apiService.get(`emails/${this.missing_info.email_guid}/document_requests`);
    if (!docs?.success || !Array.isArray(docs.data)) return;
    const files = new Map<string, any>();
    for (const doc of docs.data) {
      if (doc.uploaded_files && doc.uploaded_files.length > 0) {
        for (const f of doc.uploaded_files) {
          files.set(f.file_guid, { guid: f.file_guid, file_name: f.file_name, created_date: f.created_date, is_important: f.is_important, parent_folder_guid: f.parent_folder_guid });
        }
      } else if (doc.file_guid) {
        // Legacy: requests fulfilled before multi-file uploads only have a single file_guid
        files.set(doc.file_guid, { guid: doc.file_guid, file_name: doc.name });
      }
    }
    this.document_request_files = Array.from(files.values());
  }

  // Non-private items keep their files on the linked email (that's what the client sees); private
  // ones keep them in missing_info.file_list (the hidden Missing Info folder). The single Files
  // card shows whichever applies - the backend's POST missing_info/{guid}/files does the actual
  // routing, so the same dropzone call works for both (see MissingInfo.cs AddFiles).
  email_files: any[] = [];

  get isPrivate(): boolean {
    return !!this.missing_info?.is_private;
  }

  get displayFiles(): any[] {
    if (this.isPrivate || !this.missing_info?.email_guid) return this.missing_info?.file_list || [];
    return this.email_files;
  }

  async LoadEmailFiles() {
    this.email_files = [];
    if (this.isPrivate || !this.missing_info?.email_guid) return;
    const data: any = await this.apiService.get(`emails/files/${this.missing_info.email_guid}`);
    if (data?.success && Array.isArray(data.data)) this.email_files = data.data;
  }

  async GetTeamMembers() {
    const data: any = await this.apiService.get('team_members');
    if (data.success) this.team_members = data.data || [];
  }

  async ResolveNames() {
    const item = this.missing_info;

    // Missing info type
    if (item.type == MissingInfoTypes.outstanding_question) this.missing_info_type = 'Outstanding Question';
    else if (item.type == MissingInfoTypes.missing_document) this.missing_info_type = 'Missing Documents';
    else this.missing_info_type = 'None';

    // Client / company
    if (item.client_guid) {
      const client = await this.globalService.GetClient(item.client_guid);
      this.client_entity_name = client ? client.display_name : '';
    } else if (item.company_guid) {
      const company = await this.globalService.GetCompany(item.company_guid);
      this.client_entity_name = company ? company.name : '';
    }

    // Project
    if (item.project_guid) {
      const projectData: any = await this.apiService.get(`projects/${item.project_guid}`);
      if (projectData.success && projectData.data && projectData.data[0]) {
        this.project_title = projectData.data[0].title;
      }
    }

    // Task
    if (item.task_guid) {
      const taskData: any = await this.apiService.get(`tasks/${item.task_guid}`);
      if (taskData.success && taskData.data && taskData.data[0]) {
        this.task_title = taskData.data[0].title;
      }
    }

    // Creator
    if (item.creator_guid) {
      const creator = this.team_members.find(tm => tm.guid === item.creator_guid);
      this.recorded_by_name = creator ? creator.display_name : '';
    }

    // Intended recipient
    if (item.team_member_guid) {
      const member = this.team_members.find(tm => tm.guid === item.team_member_guid);
      this.intended_recipient_name = member ? member.display_name : '';
    }

    // Completed by
    if (item.completed_by_guid) {
      const completedBy = this.team_members.find(tm => tm.guid === item.completed_by_guid);
      this.completed_by_name = completedBy ? completedBy.display_name : '';
      if (!this.completed_by_name) {
        const client = await this.globalService.GetClient(item.completed_by_guid);
        this.completed_by_name = client ? client.display_name : '';
      }
    }
  }

  async ToggleComplete() {
    if (this.missing_info.completed) {
      // pause_option 1 = a task is paused, 2 = the whole project is paused - either one gets
      // resumed automatically once this item is marked complete, so confirm that's expected.
      if (this.missing_info.project_guid && this.missing_info.pause_option) {
        const target = this.missing_info.pause_option == 2 ? 'project' : 'task';
        const targetName = target === 'project' ? this.project_title : this.task_title;
        const confirmed = await this.ConfirmUnpauseOnComplete(target, targetName);
        if (!confirmed) {
          this.missing_info.completed = false;
          return;
        }
      }

      this.missing_info.completed_date = new Date().toISOString();
      const current_user = this.globalService.GetCurrentUser();
      this.missing_info.completed_by_guid = current_user ? current_user.guid : '';
      this.completed_by_name = current_user ? current_user.display_name : '';
    } else {
      this.missing_info.completed_date = '';
      this.missing_info.completed_by_guid = '';
      this.completed_by_name = '';
    }
    const data: any = await this.apiService.put(`missing_info/${this.missing_info.guid}`, this.missing_info);
    this.globalService.presentToastMessage(data.message, data.success);
    if (data.success) {
      this.refresh = true;
    } else {
      this.missing_info.completed = !this.missing_info.completed;
    }
  }

  // The footer button flips the flag itself (the hidden checkbox used to do this via ngModel), then
  // ToggleComplete handles the confirm / save / revert-on-failure exactly as before.
  async ToggleCompleteFromFooter() {
    this.missing_info.completed = !this.missing_info.completed;
    await this.ToggleComplete();
  }

  async ConfirmUnpauseOnComplete(target: 'project' | 'task', targetName: string): Promise<boolean> {
    return new Promise(async (resolve) => {
      const label = targetName ? `the ${target} "${targetName}"` : `the ${target}`;
      const alert = await this.alertController.create({
        header: 'Resume ' + (target === 'project' ? 'Project' : 'Task'),
        message: `Marking this as completed will unpause ${label}. Continue?`,
        buttons: [
          { text: 'Cancel', role: 'cancel', handler: () => resolve(false) },
          { text: 'Confirm', handler: () => resolve(true) }
        ]
      });
      await alert.present();
    });
  }

  async TogglePrivate() {
    const data: any = await this.apiService.put(`missing_info/${this.missing_info.guid}`, this.missing_info);
    this.globalService.presentToastMessage(data.message, data.success);
    if (!data.success) this.missing_info.is_private = !this.missing_info.is_private;
    else this.refresh = true;
  }

  async DeleteItem() {
    const has_email = !!this.missing_info?.email_guid;
    const alert = await this.alertController.create({
      header: 'Delete Missing Info',
      message: has_email
        ? `Delete "${this.missing_info.name}"? The linked ${this.missing_info_type === 'Outstanding Question' ? 'question' : 'document'} request email will be deleted as well.`
        : `Delete "${this.missing_info.name}"?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            const data: any = await this.apiService.delete(`missing_info/${this.missing_info.guid}`, {});
            this.globalService.presentToastMessage(data.message, data.success);
            if (data.success) this.modalController.dismiss({ refresh: true, deleted: true });
          }
        }
      ]
    });
    await alert.present();
  }

  async OpenEditModal() {
    const modal = await this.modalController.create({
      component: CreatePage,
      cssClass: 'large-modal',
      componentProps: {
        missing_info_item: { ...this.missing_info },
        edit_mode: true
      },
      backdropDismiss: false
    });
    modal.onDidDismiss().then((result: any) => {
      if (result.data) {
        this.refresh = true;
        this.GetData();
      }
    });
    await modal.present();
  }

  async OpenProjectDetails() {
    if (!this.missing_info?.project_guid) return;
    const modal = await this.modalController.create({
      component: ProjectDetailsPage,
      cssClass: 'large-modal',
      componentProps: { guid: this.missing_info.project_guid },
      backdropDismiss: false
    });
    await modal.present();
  }

  async OpenTaskDetails() {
    if (!this.missing_info?.task_guid) return;
    const modal = await this.modalController.create({
      component: TaskDetailsPage,
      cssClass: 'large-modal',
      componentProps: { guid: this.missing_info.task_guid },
      backdropDismiss: false
    });
    await modal.present();
  }

  async OpenEmailDetails() {
    if (!this.missing_info?.email_guid) return;
    const modal = await this.modalController.create({
      component: EmailDetailsPage,
      cssClass: 'large-modal',
      componentProps: { guid: this.missing_info.email_guid },
      backdropDismiss: false
    });
    await modal.present();
  }

  DownloadFile(file: any) {
    this.apiService.DownloadFile(file);
  }

  async presentFilePopover(e, file) {
    e.stopPropagation();
    e.preventDefault();
    const popover = await this.popoverController.create({
      component: FilePage,
      componentProps: { file: file, selected_files: [], minimal: true, show_go_to_folder: true },
      event: e,
      animated: true,
      backdropDismiss: true
    });
    popover.onDidDismiss().then((data) => {
      if (data.data == 4) this.apiService.DownloadFile(file);
      else if (data.data == 8) this.ToggleImportant(file);
      else if (data.data == 9) this.globalService.GoToFileFolder(file.parent_folder_guid);
    });
    return await popover.present();
  }

  async ToggleImportant(file: any) {
    const new_value = !file.is_important;
    const data: any = await this.apiService.put(`files/${file.guid}/important`, { important: new_value });
    if (data.success) {
      file.is_important = new_value;
      this.globalService.presentToastMessage(new_value ? 'Marked as important' : 'Removed from important', true);
    } else {
      this.globalService.presentToastMessage(data.message, false);
    }
  }

  async dropped(files: NgxFileDropEntry[]) {
    if (!this.missing_info.file_list) this.missing_info.file_list = [];

    // Resolve every dropped entry to a File first, then upload them strictly one at a time -
    // UploadFile shares a single loading overlay, so concurrent uploads leave it stuck open.
    const dropped_files: File[] = [];
    for (const droppedFile of files) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        dropped_files.push(await new Promise<File>(resolve => fileEntry.file(resolve)));
      }
    }

    for (const file of dropped_files) {
      const file_guid = await this.apiService.UploadFile(file, '0', null);
      if (file_guid == null) continue;

      // Backend routes by private state: private -> hidden Missing Info folder, non-private ->
      // attached to the linked email.
      const data: any = await this.apiService.post(`missing_info/${this.missing_info.guid}/files`, {
        file_list: [{ guid: file_guid, file_name: file.name }]
      });
      this.globalService.presentToastMessage(data.message, data.success);
      if (data.success) {
        if (this.isPrivate || !this.missing_info.email_guid) {
          this.missing_info.file_list.push({ guid: file_guid, file_name: file.name });
        } else {
          await this.LoadEmailFiles();
        }
        this.refresh = true;
      }
    }
  }

  async RemoveFile(guid: string) {
    const index = this.missing_info.file_list.findIndex((f: any) => f.guid === guid);
    if (index !== -1) {
      this.missing_info.file_list.splice(index, 1);
      await this.apiService.put(`missing_info/${this.missing_info.guid}`, this.missing_info);
      this.refresh = true;
    }
  }

  Dismiss() {
    this.modalController.dismiss({ refresh: this.refresh });
  }
}
